// @flow

import React, { Component, Fragment } from 'react';
import { Form } from 'react-form';
import { Grid, Button } from 'semantic-ui-react';
import { connect } from 'react-redux';
import type { RouterHistory, Match } from 'react-router';

import Input from 'forms/Input';
import TextArea from 'forms/TextArea';
import Select from 'forms/Select';
import {
  createSyndicate,
  fetchSyndicate,
  fetchMemberSyndicate,
  clearSyndicate,
  updateSyndicate
} from 'actions/syndicate';
import type {
  ClearSyndicateFn,
  CreateSyndicateFn,
  FetchSyndicateFn,
  FetchMemberSyndicateFn,
  UpdateSyndicateFn
} from 'actions/syndicate';

const syndicateFocusOptions = [
  {
    text: 'Seed',
    value: 'seed'
  },
  {
    text: 'Seed2',
    value: 'seed2'
  }
];

const industryOptions = [
  {
    text: 'Biotech',
    value: 'biotech'
  },
  {
    text: 'Biotech2',
    value: 'biotech2'
  }
];

const privacyOptions = [
  {
    text: 'Public',
    value: 'public'
  },
  {
    text: 'Public1',
    value: 'public1'
  }
];

const currencyOptions = [
  {
    text: 'USD',
    value: 'usd'
  },
  {
    text: 'AMD',
    value: 'amd'
  },
  {
    text: 'EURO',
    value: 'eur'
  },
  {
    text: 'RUB',
    value: 'rub'
  }
];

const horizonOptions = [
  {
    text: '3 year',
    value: '3year'
  }
];

type Props = {
  createSyndicate: CreateSyndicateFn,
  fetchSyndicate: FetchSyndicateFn,
  clearSyndicate: ClearSyndicateFn,
  fetchMemberSyndicate: FetchMemberSyndicateFn,
  updateSyndicate: UpdateSyndicateFn,
  currentSyndicate: any,
  history: RouterHistory,
  match: Match
};

class SyndicateDashboard extends Component<Props> {
  async componentDidMount() {
    const { syndicateId, memberId } = this.props.match.params;

    if (syndicateId) {
      this.mode = 'UPDATE';
      const response = await this.props.fetchSyndicate(syndicateId);

      if (response.error) {
        this.props.history.push('/');
      }
    }

    if (memberId) {
      this.mode = 'VIEW';
      const response = await this.props.fetchMemberSyndicate(memberId);

      if (response.error) {
        this.props.history.push('/');
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.currentSyndicate !== this.props.currentSyndicate) {
      this.formApi.setAllValues(this.props.currentSyndicate);
    }
  }

  componentWillUnmount() {
    this.props.clearSyndicate();
  }

  mode: string = 'CREATE';

  handleSubmit = async values => {
    const { createSyndicate, updateSyndicate, currentSyndicate } = this.props;

    const data = {
      ...values,
      user: localStorage.getItem('id')
    };

    let response;

    if (this.mode === 'UPDATE') {
      response = await updateSyndicate(currentSyndicate.id, data);
    } else {
      response = await createSyndicate({
        ...data,
        membersToInvite: values.membersToInvite
          ? values.membersToInvite.split(',').map(email => ({
              email
            }))
          : []
      });
    }

    if (!response.error) {
      this.props.history.push('/');
    }
  };

  render() {
    return (
      <div>
        <div style={{ marginBottom: '50px' }}>
          <h2>Create Investment Syndicate</h2>
          <h3>
            An investment syndicate is a private group of individual investors
            managed by the syndicate leader (you!). Investment campaigns offered
            to your syndicate are private and cannot be seen by other Leva
            users.
          </h3>
        </div>

        <Form
          className="ui form"
          onSubmit={this.handleSubmit}
          getApi={api => {
            this.formApi = api;
          }}
        >
          {formApi => (
            <form onSubmit={formApi.submitForm}>
              <Grid>
                <h4>General Information</h4>

                <Grid.Row>
                  <Grid.Column>
                    <Input fluid field="name" placeholder="Syndicate Name" />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column>
                    <TextArea
                      field="description"
                      placeholder="Description & Strategy"
                    />
                  </Grid.Column>
                </Grid.Row>

                <h4>Parameters</h4>
                <Grid.Row columns={4}>
                  <Grid.Column
                    mobile={16}
                    tablet={8}
                    computer={4}
                    style={{ marginBottom: '10px' }}
                  >
                    <Select
                      fluid
                      field="focus"
                      options={syndicateFocusOptions}
                    />
                  </Grid.Column>
                  <Grid.Column
                    mobile={16}
                    tablet={8}
                    computer={4}
                    style={{ marginBottom: '10px' }}
                  >
                    <Select fluid field="industry" options={industryOptions} />
                  </Grid.Column>
                  <Grid.Column
                    mobile={16}
                    tablet={8}
                    computer={4}
                    style={{ marginBottom: '10px' }}
                  >
                    <Select fluid field="privacy" options={privacyOptions} />
                  </Grid.Column>
                  <Grid.Column
                    mobile={16}
                    tablet={8}
                    computer={4}
                    style={{ marginBottom: '10px' }}
                  >
                    <Select fluid field="currency" options={currencyOptions} />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row columns={3}>
                  <Grid.Column
                    mobile={16}
                    tablet={8}
                    computer={4}
                    style={{ marginBottom: '10px' }}
                  >
                    <Input
                      type="number"
                      fluid
                      field="capitalRaised"
                      placeholder="Capital to be raised"
                    />
                  </Grid.Column>
                  <Grid.Column
                    mobile={16}
                    tablet={8}
                    computer={4}
                    style={{ marginBottom: '10px' }}
                  >
                    <Select fluid field="horizon" options={horizonOptions} />
                  </Grid.Column>
                  <Grid.Column
                    mobile={16}
                    tablet={8}
                    computer={4}
                    style={{ marginBottom: '10px' }}
                  >
                    <Input
                      type="number"
                      fluid
                      field="minCommitment"
                      placeholder="minimumCommitment"
                    />
                  </Grid.Column>
                </Grid.Row>

                <h4>Leadership Commitment</h4>
                <Grid.Row>
                  <Grid.Column>
                    <Input
                      type="number"
                      fluid
                      field="leadershipCommitment"
                      placeholder="Leadership Commitment"
                    />
                  </Grid.Column>
                </Grid.Row>

                {this.mode === 'CREATE' && (
                  <Fragment>
                    <h4>Invite Syndicate Members</h4>
                    <Grid.Row columns={1}>
                      <Grid.Column>
                        <TextArea
                          field="membersToInvite"
                          placeholder="Type Email addresses through commas"
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Fragment>
                )}
                <Grid.Row>
                  <Grid.Column>
                    <TextArea
                      field="personalNote"
                      placeholder="Personal Note"
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>

              <Grid.Row style={{ marginTop: '50px' }}>
                <Button color="green" size="large" fluid>
                  Send Invitation
                </Button>
              </Grid.Row>
            </form>
          )}
        </Form>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { memberId } = ownProps.match.params;
  const { currentSyndicate, entities } = state;
  const memberSyndicate = entities.memberSyndicates[memberId];

  const id = memberId
    ? memberSyndicate && memberSyndicate.syndicate
    : currentSyndicate;

  return {
    currentSyndicate: state.entities.syndicates[id]
  };
};

export default connect(
  mapStateToProps,
  {
    createSyndicate,
    fetchSyndicate,
    clearSyndicate,
    updateSyndicate,
    fetchMemberSyndicate
  }
)(SyndicateDashboard);



// WEBPACK FOOTER //
// ./src/screens/SyndicateForm/index.js